import * as TabsPrimitive from "@radix-ui/react-tabs";
import { LucideIcon } from "lucide-react";
import * as React from "react";

import { cn } from "~/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

/**
 * AdaptiveTabs is an underline-style tab bar that adapts its content to the
 * width of its container (not the viewport). It is meant for panels whose
 * width changes independently of the screen — such as the doclist side panel,
 * which can be collapsed, expanded, or rendered full-width on mobile.
 *
 * Three responsive tiers, measured from the *per-tab* width — the width each
 * trigger actually gets (triggers are `flex-1`, so this is roughly the list's
 * available width divided by the number of tabs). Keying off per-tab width
 * rather than the raw container width means the bar collapses to icons when
 * there are many narrow tabs (instead of truncating their labels):
 * - `icons`: below `iconOnlyMaxTabWidth` — icon only (label in tooltip)
 * - `text`:  between the two thresholds — icon stacked above label
 * - `both`:  at/above `iconAndTextMinTabWidth` — icon + label side by side
 *
 * An animated underline indicator slides and resizes to follow the active tab.
 */

type TabTier = "icons" | "text" | "both";

/**
 * Per-tab width (px) below which a tab collapses to icon-only. A typical
 * desktop doclist panel (~388px / 5 tabs ≈ 77px each) lands in the "text"
 * tier (icon stacked above label).
 */
export const ICON_ONLY_MAX_TAB_WIDTH = 65;
/** Per-tab width (px) at/above which icon + label are shown side by side. */
export const ICON_AND_TEXT_MIN_TAB_WIDTH = 100;

/** Delay between adjacent letters for the active-label colour sweep. */
const LETTER_SWEEP_STAGGER_MS = 22;

const resolveTier = (
  perTabWidth: number,
  iconOnlyMaxTabWidth: number,
  iconAndTextMinTabWidth: number
): TabTier => {
  if (perTabWidth < iconOnlyMaxTabWidth) return "icons";
  if (perTabWidth >= iconAndTextMinTabWidth) return "both";
  return "text";
};

const AdaptiveTabsTierContext = React.createContext<TabTier>("both");

const useAdaptiveTabsTier = () => React.useContext(AdaptiveTabsTierContext);

const AdaptiveTabs = TabsPrimitive.Root;

interface AdaptiveTabsListProps extends React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.List
> {
  /** Per-tab width below which only icons are shown. Defaults to {@link ICON_ONLY_MAX_TAB_WIDTH}. */
  iconOnlyMaxTabWidth?: number;
  /** Per-tab width at/above which icon + label are shown. Defaults to {@link ICON_AND_TEXT_MIN_TAB_WIDTH}. */
  iconAndTextMinTabWidth?: number;
}

const AdaptiveTabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  AdaptiveTabsListProps
>(
  (
    {
      className,
      children,
      iconOnlyMaxTabWidth = ICON_ONLY_MAX_TAB_WIDTH,
      iconAndTextMinTabWidth = ICON_AND_TEXT_MIN_TAB_WIDTH,
      ...props
    },
    forwardedRef
  ) => {
    const listRef = React.useRef<HTMLDivElement | null>(null);
    const indicatorRef = React.useRef<HTMLSpanElement | null>(null);
    const [tier, setTier] = React.useState<TabTier>("both");

    const setRefs = React.useCallback(
      (node: HTMLDivElement | null) => {
        listRef.current = node;
        if (typeof forwardedRef === "function") {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef]
    );

    const positionIndicator = React.useCallback((animate: boolean) => {
      const list = listRef.current;
      const indicator = indicatorRef.current;
      if (!list || !indicator) return;

      // Query by `data-state` on direct children rather than `role="tab"`:
      // dnd-kit spreads `role="button"` (overriding Radix's `role="tab"`), and
      // inner tooltip triggers also carry their own `data-state`. Scoping to
      // direct children avoids both conflicts.
      const active = list.querySelector<HTMLElement>(
        ":scope > [data-state='active']"
      );
      if (!active) {
        indicator.style.opacity = "0";
        return;
      }

      const apply = () => {
        indicator.style.opacity = "1";
        indicator.style.width = `${active.offsetWidth}px`;
        indicator.style.transform = `translateX(${active.offsetLeft}px)`;
      };

      if (animate) {
        apply();
        return;
      }

      // Skip the transition for the initial placement so the indicator does
      // not visibly slide in from the left edge on first paint.
      const previousTransition = indicator.style.transition;
      indicator.style.transition = "none";
      apply();
      // Force a reflow so the no-transition styles are committed before the
      // transition is restored.
      void indicator.offsetHeight;
      indicator.style.transition = previousTransition;
    }, []);

    React.useLayoutEffect(() => {
      const list = listRef.current;
      if (!list) return;

      let isFirstMeasure = true;
      const update = () => {
        // Measure the per-tab width, not the raw container width: triggers
        // share the list's width equally via `flex-1`, so one trigger's
        // rendered width (already net of the list's padding) is the budget
        // each tab gets. Tabs carry a Radix-managed `data-state`, which —
        // unlike `role` — dnd-kit never overrides, so it reliably selects them.
        const triggers = list.querySelectorAll<HTMLElement>(
          ":scope > [data-state]"
        );
        const perTabWidth =
          triggers.length > 0 ? triggers[0].offsetWidth : list.clientWidth;
        setTier(
          resolveTier(perTabWidth, iconOnlyMaxTabWidth, iconAndTextMinTabWidth)
        );
        positionIndicator(!isFirstMeasure);
        isFirstMeasure = false;
      };

      update();

      const resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(list);

      // Re-measure when the active tab changes (Radix flips data-state) or when
      // tabs are added/removed (e.g. guest/view-only modes hide some tabs):
      // changing the tab count changes the per-tab width, so the tier — not
      // just the indicator position — may need to update.
      const mutationObserver = new MutationObserver(update);
      mutationObserver.observe(list, {
        attributes: true,
        attributeFilter: ["data-state"],
        childList: true,
        subtree: true,
      });

      return () => {
        resizeObserver.disconnect();
        mutationObserver.disconnect();
      };
    }, [iconOnlyMaxTabWidth, iconAndTextMinTabWidth, positionIndicator]);

    return (
      <AdaptiveTabsTierContext.Provider value={tier}>
        <TabsPrimitive.List
          ref={setRefs}
          className={cn(
            "relative flex w-full items-stretch border-b border-border text-muted-foreground",
            className
          )}
          {...props}
        >
          {children}
          <span
            ref={indicatorRef}
            aria-hidden
            style={{ opacity: 0 }}
            className="pointer-events-none absolute bottom-0 left-0 h-0.5 w-0 rounded-full bg-foreground transition-[transform,width,opacity] duration-300 ease-out motion-reduce:transition-none"
          />
        </TabsPrimitive.List>
      </AdaptiveTabsTierContext.Provider>
    );
  }
);
AdaptiveTabsList.displayName = "AdaptiveTabsList";

/**
 * Renders a label whose letters fade from muted to active colour with a small
 * left-to-right stagger. The colour is inherited from the trigger, so the
 * sweep plays automatically whenever the trigger's active state flips.
 *
 * The wrapper is `aria-hidden` because splitting the word into per-letter spans
 * would otherwise make assistive tech announce it letter-by-letter; the trigger
 * always carries an `aria-label` with the intact word for the accessible name.
 */
const SweepLabel = React.forwardRef<HTMLSpanElement, { text: string }>(
  ({ text }, ref) => (
    <span ref={ref} aria-hidden className="truncate">
      {Array.from(text).map((char, index) => (
        <span
          key={`${char}-${index}`}
          className="transition-colors duration-200 ease-out motion-reduce:transition-none"
          style={{ transitionDelay: `${index * LETTER_SWEEP_STAGGER_MS}ms` }}
        >
          {char}
        </span>
      ))}
    </span>
  )
);
SweepLabel.displayName = "SweepLabel";

interface AdaptiveTabsTriggerProps extends React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.Trigger
> {
  /** Resolved (already-translated) label text. */
  label: string;
  icon?: LucideIcon;
  /** Animate the label colour with a letter-by-letter sweep. Defaults to true. */
  animateLabel?: boolean;
}

/**
 * Detects whether a text element is truncated (scrollWidth > clientWidth).
 * Uses a callback ref so the observer re-attaches when the element
 * mounts/unmounts (e.g. during tier transitions).
 */
const useIsTruncated = (): [boolean, (el: HTMLElement | null) => void] => {
  const [el, setEl] = React.useState<HTMLElement | null>(null);
  const [truncated, setTruncated] = React.useState(false);

  React.useEffect(() => {
    if (!el) {
      setTruncated(false);
      return;
    }

    const check = () => setTruncated(el.scrollWidth > el.clientWidth);
    check();

    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [el]);

  return [truncated, setEl];
};

const AdaptiveTabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  AdaptiveTabsTriggerProps
>(({ className, label, icon: Icon, animateLabel = true, ...props }, ref) => {
  const tier = useAdaptiveTabsTier();
  const showIcon = true;
  const showLabel = tier !== "icons";
  const [isTruncated, labelRef] = useIsTruncated();

  const needsTooltip = !showLabel || isTruncated;

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      aria-label={label}
      className={cn(
        "group relative flex min-w-0 flex-1 items-center justify-center whitespace-nowrap px-1 pb-0 pt-1.5 text-xs font-medium text-muted-foreground ring-offset-background transition-colors hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset disabled:pointer-events-none disabled:opacity-50 data-[state=active]:text-foreground",
        tier === "text" ? "flex-col gap-0.5" : "gap-1.5",
        className
      )}
      {...props}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              "flex min-w-0 items-center justify-center",
              tier === "text" ? "flex-col gap-0.5 text-center" : "gap-1.5"
            )}
          >
            {showIcon && Icon && <Icon className="h-4 w-4 shrink-0" />}
            {showLabel &&
              (animateLabel ? (
                <SweepLabel ref={labelRef} text={label} />
              ) : (
                <span ref={labelRef} className="truncate">
                  {label}
                </span>
              ))}
          </span>
        </TooltipTrigger>
        {needsTooltip && (
          <TooltipContent side="bottom">
            <p>{label}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TabsPrimitive.Trigger>
  );
});
AdaptiveTabsTrigger.displayName = "AdaptiveTabsTrigger";

const AdaptiveTabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
));
AdaptiveTabsContent.displayName = "AdaptiveTabsContent";

export {
  AdaptiveTabs,
  AdaptiveTabsContent,
  AdaptiveTabsList,
  AdaptiveTabsTrigger,
};
export type { AdaptiveTabsListProps, AdaptiveTabsTriggerProps, TabTier };
